/**
 * Audio Attachment Chip
 *
 * Visual indicator that a recorded audio file is attached to the input.
 * Shows filename, duration, and file size with a remove button.
 */

import { X, Mic } from 'lucide-react';
import type { AudioRecording } from '../../hooks/useAudioRecorder';

interface AudioAttachmentChipProps {
  recording: AudioRecording;
  onRemove: () => void;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

export function AudioAttachmentChip({ recording, onRemove }: AudioAttachmentChipProps) {
  return (
    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-bg-hover border border-border text-xs text-text-muted">
      <Mic className="w-3 h-3 text-accent-orange shrink-0" />
      <span className="font-mono text-text-primary truncate max-w-[160px]">
        {recording.filename}
      </span>
      <span className="text-text-muted/60 shrink-0">
        {formatDuration(recording.duration)} &middot; {formatSize(recording.size)}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="p-0.5 rounded-sm hover:bg-bg-primary hover:text-red-400 transition-colors shrink-0"
        title="Remove recording"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
